import type { TFunction } from 'i18next';
import { CampaignV2Response, CampaignV2Status } from '@/services/endpoints/campaigns/types';
import { translateI18nValues } from '@/utils/translationUtil';

export type CampaignListStatus = 'active' | 'paused' | 'completed' | 'scheduled' | 'failed' | 'draft';

export const STATUS_STYLES: Record<CampaignListStatus, string> = {
  active: 'bg-emerald-100 text-emerald-700',
  paused: 'bg-amber-100 text-amber-700',
  completed: 'bg-slate-200 text-slate-700',
  scheduled: 'bg-sky-100 text-sky-700',
  failed: 'bg-red-50 text-red-700',
  draft: 'bg-slate-100 text-slate-700',
};

/** i18n keys, namespaced so both `useTranslation('campaign')` and the table TFunction resolve them. */
export const STATUS_LABEL_KEYS: Record<CampaignListStatus, string> = {
  active: 'campaign:campaignStatus.active',
  paused: 'campaign:campaignStatus.paused',
  completed: 'campaign:campaignStatus.completed',
  scheduled: 'campaign:campaignStatus.scheduled',
  failed: 'campaign:campaignStatus.failed',
  draft: 'campaign:campaignStatus.draft',
};

export const mapCampaignStatus = (status: CampaignV2Status): CampaignListStatus => {
  if (status === 'ACTIVE') {
    return 'active';
  }
  if (status === 'PAUSED') {
    return 'paused';
  }
  if (status === 'COMPLETED') {
    return 'completed';
  }
  if (status === 'SCHEDULED') {
    return 'scheduled';
  }
  if (status === 'FAILED') {
    return 'failed';
  }

  return 'draft';
};

/**
 * User-facing reason of a FAILED campaign. Insufficient credits come as a stable code; any other
 * reason is the connector's TRANSLATIONS.json key (translated via /v1/connectors/translations), or
 * plain text from an API that predates the keys.
 */
export const campaignFailureReason = (
  campaign: Pick<CampaignV2Response, 'errorCode' | 'errorMessage'>,
  t: TFunction | ((key: string) => string),
  lang?: string
): string => {
  if (campaign.errorCode === 'INSUFFICIENT_CREDITS') return t('campaign:campaignFailure.insufficientCredits');
  return campaign.errorMessage ? translateI18nValues(campaign.errorMessage, lang) : t('campaign:campaignFailure.generic');
};
